import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Line, Bar } from 'react-chartjs-2';
import { Wifi } from 'lucide-react';
import { SectionLabel, RefreshBtn, EmptyChart, chartOptions, lagColor, heapColor } from './shared';

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Node.js health ────────────────────────────────────────────────────────────

function NodeHealthSection({ theme }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const intervalRef           = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/admin/platform-stats')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load platform stats'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const trend    = data?.trend ?? [];
  const heapPct  = data ? Math.round(parseFloat(data.node.heapUsedMb) / parseFloat(data.node.heapTotalMb) * 100) : null;
  const lagMs    = data ? parseFloat(data.node.eventLoopLagMs) : null;
  const labels   = trend.map(r => new Date(r.captured_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
  const uptimeStr = data ? (() => {
    const s = Math.floor(data.node.uptimeSec);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })() : '—';

  const badge = (label, value, color) => (
    <div style={{ background: theme.surfaceAlt, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || theme.text, lineHeight: 1 }}>{value ?? '—'}</div>
    </div>
  );

  const memChart = {
    labels,
    datasets: [
      { label: 'Heap used', data: trend.map(r => parseFloat(r.heap_used_mb)), borderColor: '#667eea', backgroundColor: 'rgba(102,126,234,0.1)', tension: 0.3, fill: true, pointRadius: 2 },
      { label: 'RSS',       data: trend.map(r => parseFloat(r.rss_mb)),       borderColor: '#764ba2', backgroundColor: 'transparent', tension: 0.3, fill: false, pointRadius: 0, borderDash: [4, 3] },
    ],
  };
  const lagChart = {
    labels,
    datasets: [{ label: 'Lag (ms)', data: trend.map(r => parseFloat(r.event_loop_lag_ms)), borderColor: lagColor(lagMs), backgroundColor: `${lagColor(lagMs)}18`, tension: 0.3, fill: true, pointRadius: 2 }],
  };

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme} action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: theme.textMuted }}>Auto-refreshes every 30s</span>
          <RefreshBtn onClick={load} loading={loading} theme={theme} />
        </div>
      }>
        Node.js Health
      </SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
        {badge('Heap used',  data ? `${data.node.heapUsedMb} MB`    : '—', heapColor(heapPct))}
        {badge('Heap total', data ? `${data.node.heapTotalMb} MB`   : '—')}
        {badge('RSS',        data ? `${data.node.rssMb} MB`         : '—')}
        {badge('Event loop', data ? `${data.node.eventLoopLagMs} ms`: '—', lagColor(lagMs))}
        {badge('Uptime',     uptimeStr)}
        {badge('Node',       data ? data.node.nodeVersion           : '—')}
      </div>
      {trend.length > 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '14px 16px 10px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>Memory — 24h (MB)</div>
            <div style={{ height: 140 }}><Line data={memChart} options={chartOptions(theme)} /></div>
          </div>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '14px 16px 10px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>Event loop lag — 24h (ms)</div>
            <div style={{ height: 140 }}><Line data={lagChart} options={chartOptions(theme)} /></div>
          </div>
        </div>
      )}
      {trend.length <= 1 && (
        <div style={{ fontSize: 12, color: theme.textMuted }}>Trend data populates after the first 10-minute snapshot interval.</div>
      )}
    </div>
  );
}

// ── WebSocket metrics ─────────────────────────────────────────────────────────

function WsMetricsSection({ theme }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const intervalRef           = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/admin/ws-metrics')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load WebSocket metrics'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const live    = data?.live ?? {};
  const rollups = data?.rollups ?? {};
  const trend   = data?.trend ?? [];
  const labels  = trend.map(r => new Date(r.captured_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));

  const trendChart = {
    labels,
    datasets: [{ label: 'Connections', data: trend.map(r => r.connections), borderColor: '#667eea', backgroundColor: 'rgba(102,126,234,0.1)', tension: 0.3, fill: true, pointRadius: 2 }],
  };

  const statCard = (label, value, sub) => (
    <div style={{ background: theme.surfaceAlt, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Wifi size={13} color={theme.textMuted} />
        <span style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: theme.text, lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme} action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: theme.textMuted }}>Auto-refreshes every 30s</span>
          <RefreshBtn onClick={load} loading={loading} theme={theme} />
        </div>
      }>
        WebSocket Connections
      </SectionLabel>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
        {statCard('Live',     live.totalConnected ?? '—', `${live.gigRooms?.length ?? 0} gig room${live.gigRooms?.length !== 1 ? 's' : ''} · ${live.artistRoomsCount ?? 0} artist`)}
        {statCard('Peak 1h',  rollups.peak_1h  ?? '—')}
        {statCard('Peak 24h', rollups.peak_24h ?? '—')}
        {statCard('Avg 24h',  rollups.avg_24h  ?? '—')}
      </div>

      {live.gigRooms?.length > 0 && (
        <div style={{ marginBottom: 16, borderRadius: 8, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
            <thead>
              <tr>
                {['Artist', 'Room', 'Connections'].map(h => (
                  <th key={h} style={{ padding: '8px 14px', fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', background: theme.surfaceAlt, borderBottom: `2px solid ${theme.border}`, textAlign: h === 'Connections' ? 'center' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {live.gigRooms.map((r, i) => (
                <tr key={r.room} onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 600, color: theme.text, borderBottom: i < live.gigRooms.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{r.artistName}</td>
                  <td style={{ padding: '9px 14px', fontSize: 11, color: theme.textMuted, fontFamily: 'monospace', borderBottom: i < live.gigRooms.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{r.room}</td>
                  <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 600, color: theme.text, textAlign: 'center', borderBottom: i < live.gigRooms.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {trend.length > 1
        ? <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '14px 16px 10px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>24h connection trend</div>
            <div style={{ height: 140 }}><Line data={trendChart} options={chartOptions(theme)} /></div>
          </div>
        : <div style={{ fontSize: 12, color: theme.textMuted }}>Trend data populates after the first 10-minute snapshot interval.</div>}
    </div>
  );
}

// ── Platform charts ───────────────────────────────────────────────────────────

function PlatformChartsSection({ theme }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/admin/analytics')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 32 }}>Loading analytics…</div>;
  if (!data) return null;

  const opts = chartOptions(theme);
  const card = { background: theme.surface, borderRadius: 8, border: `1px solid ${theme.border}`, padding: '16px 16px 12px' };

  const reqDataset = {
    labels: data.requestsOverTime.map(r => new Date(r.day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })),
    datasets: [{ label: 'Requests', data: data.requestsOverTime.map(r => r.count), borderColor: '#667eea', backgroundColor: 'rgba(102,126,234,0.12)', tension: 0.4, fill: true, pointRadius: 2 }],
  };
  const signupDataset = {
    labels: data.signupsOverTime.map(r => new Date(r.week).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })),
    datasets: [{ label: 'Sign-ups', data: data.signupsOverTime.map(r => r.count), borderColor: '#22c55e', backgroundColor: 'rgba(34,197,94,0.12)', tension: 0.4, fill: true, pointRadius: 2 }],
  };
  const fullDow = DOW_LABELS.map((label, i) => ({ label, count: data.peakByDow.find(r => r.dow === i)?.count ?? 0 }));
  const dowDataset = { labels: fullDow.map(d => d.label), datasets: [{ label: 'Requests', data: fullDow.map(d => d.count), backgroundColor: '#764ba2', borderRadius: 4 }] };
  const topSongsDataset = {
    labels: data.topSongs.map(s => s.title.length > 22 ? s.title.slice(0, 20) + '…' : s.title),
    datasets: [{ label: 'Requests', data: data.topSongs.map(s => s.request_count), backgroundColor: '#667eea', borderRadius: 4 }],
  };

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>Requests — last 90 days</div>
          <div style={{ height: 180 }}>{data.requestsOverTime.length > 0 ? <Line data={reqDataset} options={opts} /> : <EmptyChart theme={theme} />}</div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>Sign-ups — last 90 days (weekly)</div>
          <div style={{ height: 180 }}>{data.signupsOverTime.length > 0 ? <Line data={signupDataset} options={opts} /> : <EmptyChart theme={theme} />}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>Peak days (all-time)</div>
          <div style={{ height: 180 }}><Bar data={dowDataset} options={opts} /></div>
        </div>
        <div style={card}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 10 }}>Top requested songs (all tenants)</div>
          <div style={{ height: 180 }}>
            {data.topSongs.length > 0
              ? <Bar data={topSongsDataset} options={{ ...opts, indexAxis: 'y', scales: { ...opts.scales, x: { ...opts.scales.x, ticks: { ...opts.scales.x.ticks, font: { size: 10 } } }, y: { ticks: { color: theme.textMuted, font: { size: 10 } }, grid: { color: theme.border } } } }} />
              : <EmptyChart theme={theme} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export default function InfraTab({ theme }) {
  return (
    <>
      <NodeHealthSection theme={theme} />
      <WsMetricsSection theme={theme} />
      <PlatformChartsSection theme={theme} />
    </>
  );
}
